import { ApolloLink, type Operation } from "@apollo/client";
import { getOperationAST } from "graphql";
import { logGreyError } from "owa-analytics";
import type { Resolvers, ResolverContext } from "owa-graph-schema";
import executeGraphLink from "../links/executeGraphLink";
import { getMailboxInfoFromOperationStrict } from "owa-request-options";
import { type ResolverImports } from "../factories/createGraphServerLink";
import { capitalizeOp } from "../util/getOperationType";

export function isUnifiedInboxOperation(operation: Operation): boolean {
    const mailboxInfo = getMailboxInfoFromOperationStrict(operation);
    return mailboxInfo?.type === "UnifiedInbox";
}

export function unifiedInboxServerLink(context: Record<string, any>, getResolvers: () => Promise<ResolverImports>): ApolloLink {
    async function getResolversProfile(operation: Operation) {
        const resolverImports = await getResolvers();
        
        const resolvers = resolverImports.unifiedInbox || {};
        const dataSource = undefined;
        const isOfflineEnabled = !!resolverImports.offline;
        
        logMissingUnifiedInboxResolvers(operation, resolvers);
        
        return {
            resolvers,
            dataSource,
            isOfflineEnabled,
        };
    }
    
    return new ApolloLink(operation => executeGraphLink(() => getResolversProfile(operation), context).request(operation));
}

/**
 * Unified Inbox operations never fall back to the web resolvers or the gateway, so a top-level field
 * without a Unified Inbox resolver silently resolves to null with no network call. Flag those so
 * callers that pass the Unified Inbox mailbox instead of a per-account mailbox can be found.
 */
export function logMissingUnifiedInboxResolvers(operation: Operation, resolvers: Resolvers<ResolverContext>): void {
    const opNode = getOperationAST(operation.query, operation.operationName);
    if (!opNode) {
        return;
    }
    
    const operationType = capitalizeOp(opNode.operation) as keyof Resolvers<ResolverContext>;
    // Avoid constructing the large union produced by indexing the resolver type with a
    // computed operation type; this code only needs to inspect root fields by name.
    const resolverMap = resolvers as unknown as Record<string, Record<string, unknown> | undefined>;
    const rootResolvers = resolverMap[operationType] || {};
    for (const selection of opNode.selectionSet.selections) {
        if (selection.kind !== "Field" ||
            selection.name.value.startsWith("__") ||
            selection.directives?.some(directive => directive.name.value === "client")) {
            continue;
        }
        
        const fieldName = selection.name.value;
        if (!rootResolvers[fieldName]) {
            logGreyError("UnifiedInboxMissingResolver", getMailboxInfoFromOperationStrict(operation), new Error("No Unified Inbox resolver for operation field"), {
                operationName: operation.operationName,
                operationType,
                fieldName,
            });
        }
    }
}
